import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { ConfirmDialog } from "./ConfirmDialog";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CheckSquare, Square, Plus, Trash2, Calendar } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { formatDate } from "@/lib/format";

const inputCls = "rounded-xl h-11";
const selectCls =
  "rounded-xl h-11 w-full px-3 text-sm bg-white border border-gray-300 text-brand-navy focus:border-brand-orange focus:outline-none";

/**
 * TaskList component. If dealId is provided, tasks are scoped to that deal.
 * Props: dealId (optional), team=[], showAdd (default true), title.
 */
export function TaskList({ dealId, team = [], showAdd = true, title = "Tasks", onCountChange }) {
  const { admin } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [toDelete, setToDelete] = useState(null);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (dealId) params.deal_id = dealId;
      const { data } = await api.get("/admin/tasks", { params });
      setItems(data.items || []);
      onCountChange?.(data.count || 0);
    } catch {
      toast.error("Failed to load tasks");
    } finally {
      setLoading(false);
    }
  }, [dealId, onCountChange]);

  useEffect(() => { fetch(); }, [fetch]);

  const toggle = async (task) => {
    const nextStatus = task.status === "done" ? "pending" : "done";
    try {
      const { data } = await api.patch(`/admin/tasks/${task.id}`, { status: nextStatus });
      setItems((arr) => arr.map((x) => (x.id === task.id ? data : x)));
    } catch {
      toast.error("Failed to update");
    }
  };

  const doDelete = async () => {
    if (!toDelete) return;
    try {
      await api.delete(`/admin/tasks/${toDelete.id}`);
      setItems((arr) => arr.filter((x) => x.id !== toDelete.id));
      toast.success("Task deleted");
    } catch {
      toast.error("Failed to delete");
    } finally {
      setToDelete(null);
    }
  };

  return (
    <section className="bg-white border border-brand-navy/10 rounded-2xl p-6" data-testid="task-list">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <CheckSquare size={16} className="text-brand-orange" />
          <h2 className="text-sm font-semibold uppercase tracking-[0.25em] text-brand-navy">{title}</h2>
        </div>
        {showAdd && (
          <button
            onClick={() => setShowAddForm(true)}
            className="inline-flex items-center gap-1 bg-brand-orange text-white px-3 py-1.5 text-xs font-semibold rounded-full hover:bg-brand-orange-hover transition-colors"
            data-testid="task-add-button"
          >
            <Plus size={12} /> Add task
          </button>
        )}
      </div>

      {loading && <p className="text-sm text-brand-slate/60">Loading…</p>}
      {!loading && items.length === 0 && (
        <p className="text-sm text-brand-slate/60 py-6 text-center" data-testid="task-empty">
          No tasks yet. Add a follow-up so nothing slips.
        </p>
      )}

      <ul className="space-y-2">
        {items.map((t) => (
          <li
            key={t.id}
            className={`flex items-start gap-3 p-3 rounded-xl border transition-colors ${
              t.status === "done"
                ? "bg-brand-cream/40 border-brand-navy/10 opacity-70"
                : t.is_overdue
                ? "bg-red-50 border-red-200"
                : "bg-white border-brand-navy/10 hover:border-brand-orange/40"
            }`}
            data-testid={`task-item-${t.id}`}
          >
            <button
              onClick={() => toggle(t)}
              className="mt-0.5 shrink-0"
              aria-label={t.status === "done" ? "Mark pending" : "Mark done"}
              data-testid={`task-toggle-${t.id}`}
            >
              {t.status === "done"
                ? <CheckSquare size={18} className="text-emerald-600" />
                : <Square size={18} className="text-brand-slate/60 hover:text-brand-orange" />}
            </button>
            <div className="flex-1 min-w-0">
              <p className={`text-sm font-medium ${t.status === "done" ? "line-through text-brand-slate/60" : "text-brand-navy"}`}>
                {t.title}
              </p>
              {t.description && (
                <p className="text-xs text-brand-slate/70 mt-0.5 whitespace-pre-wrap">{t.description}</p>
              )}
              <div className="mt-1 flex items-center gap-3 flex-wrap text-[11px] text-brand-slate/60">
                {t.due_date && (
                  <span className={`inline-flex items-center gap-1 ${t.is_overdue ? "text-red-600 font-semibold" : ""}`}>
                    <Calendar size={10} /> {formatDate(t.due_date)}
                    {t.is_overdue && " · overdue"}
                  </span>
                )}
                {t.owner_name && <span>· {t.owner_name}</span>}
                {t.deal_title && !dealId && <span>· {t.deal_title}</span>}
              </div>
            </div>
            <button
              onClick={() => setToDelete(t)}
              className="text-brand-slate/40 hover:text-red-600"
              aria-label="Delete"
              data-testid={`task-delete-${t.id}`}
            >
              <Trash2 size={12} />
            </button>
          </li>
        ))}
      </ul>

      <AddTaskDialog
        open={showAddForm}
        onOpenChange={setShowAddForm}
        team={team}
        defaultOwnerId={admin?.id}
        dealId={dealId}
        onCreated={(t) => setItems((arr) => [t, ...arr])}
      />

      <ConfirmDialog
        open={!!toDelete}
        onOpenChange={(o) => !o && setToDelete(null)}
        title="Delete this task?"
        description={toDelete?.title}
        confirmLabel="Delete"
        destructive
        onConfirm={doDelete}
      />
    </section>
  );
}

function AddTaskDialog({ open, onOpenChange, team, defaultOwnerId, dealId, onCreated }) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [ownerId, setOwnerId] = useState(defaultOwnerId || "");
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (open && defaultOwnerId && !ownerId) setOwnerId(defaultOwnerId); }, [open, defaultOwnerId, ownerId]);

  const reset = () => { setTitle(""); setDescription(""); setDueDate(""); setOwnerId(defaultOwnerId || ""); setSaving(false); };
  const close = (o) => { if (!o) reset(); onOpenChange(o); };

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim()) { toast.error("Title required"); return; }
    setSaving(true);
    try {
      const payload = { title: title.trim() };
      if (description.trim()) payload.description = description.trim();
      if (dueDate) payload.due_date = dueDate;
      if (ownerId) payload.owner_id = ownerId;
      if (dealId) payload.deal_id = dealId;
      const { data } = await api.post("/admin/tasks", payload);
      toast.success("Task added");
      onCreated?.(data);
      close(false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to create");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="sm:max-w-lg" data-testid="add-task-dialog">
        <DialogHeader>
          <DialogTitle className="text-brand-navy">Add task</DialogTitle>
          <DialogDescription>Create a follow-up so nothing slips through.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="flex flex-col gap-1.5">
            <Label className="text-sm text-brand-navy">Title <span className="text-brand-orange">*</span></Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} required className={inputCls} data-testid="task-input-title" />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label className="text-sm text-brand-navy">Description</Label>
            <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} className="rounded-xl w-full px-3 py-2 text-sm bg-white border border-gray-300 text-brand-navy focus:border-brand-orange focus:outline-none" data-testid="task-input-description" />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="flex flex-col gap-1.5">
              <Label className="text-sm text-brand-navy">Due date</Label>
              <Input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} className={inputCls} data-testid="task-input-due" />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label className="text-sm text-brand-navy">Assigned to</Label>
              <select value={ownerId} onChange={(e) => setOwnerId(e.target.value)} className={selectCls} data-testid="task-input-owner">
                {team.map((m) => <option key={m.id} value={m.id}>{m.name || m.email}</option>)}
              </select>
            </div>
          </div>
          <DialogFooter>
            <button type="button" onClick={() => close(false)} className="inline-flex items-center gap-2 border border-brand-navy/20 px-5 py-2.5 text-xs font-semibold text-brand-navy hover:bg-brand-cream rounded-full">Cancel</button>
            <button type="submit" disabled={saving || !title.trim()} className="inline-flex items-center gap-2 bg-brand-orange text-white px-6 py-2.5 text-xs font-semibold hover:bg-brand-orange-hover rounded-full disabled:opacity-60" data-testid="task-submit">
              {saving ? "Adding…" : "Add task"}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
